/**
 * Party panel (docs/design/multiplayer.md §3): create a party and share its invite link (or join
 * one by link / code), see who is in it and which hero each plays (free slots are bots), Ready,
 * and, for the leader, the key and Start. Opened from the lobby's Party button (online only), or
 * by main.ts when the game was opened from an invite link. The run itself starts when the room
 * says so (main.ts listens on the PartyClient).
 */
import type { MetaContext } from '../context';
import type { PartyClient, PartyEvent } from '../../net/partyClient';
import { type RoomView, inviteUrl, parseInvite } from '../../net/protocol';
import { el } from '../ui/dom';
import { classCrest, ghostButton, kmModal, kmToast, primaryButton, secondaryButton, stepper } from '../kmmo';
import { heroFor } from '../heroes';
import './party.css';

export function openPartyPanel(ctx: Pick<MetaContext, 'party' | 'content'>): void {
  const host = ctx.party;
  if (!host) return void kmToast('Parties need the online server.', 'error');
  let unsub = (): void => {};
  const m = kmModal('Party', { wide: true, onClose: () => unsub() });
  m.el.classList.add('kpt');

  const draw = (): void => {
    unsub();
    unsub = () => {};
    m.body.replaceChildren();
    m.footer.replaceChildren();
    const party = host.current();
    if (!party) return drawNoParty();
    unsub = party.subscribe((e: PartyEvent) => {
      // Left, or refused (full / in a key / expired): main.ts dropped it; back to Create / Join.
      if (host.current() !== party) return void draw();
      if (e.type === 'room' || e.type === 'status') drawParty(party);
      if (e.type === 'error') kmToast(e.msg, 'error', 4000);
      if (e.type === 'start') m.close();
    });
    drawParty(party);
  };

  function drawNoParty(): void {
    el('p', 'km-modal-text', m.body, 'Play a key with friends. Up to 4 players; bots fill the empty slots. Everyone keeps their own rewards.');
    const row = el('div', 'kpt-join', m.body);
    const input = el('input', 'kpt-code-input', row);
    input.placeholder = 'Invite link or code';
    input.autocapitalize = 'characters';
    input.spellcheck = false;
    const err = el('div', 'km-form-error', m.body);
    const join = (): void => {
      const code = parseInvite(input.value);
      if (!code) {
        err.textContent = 'Paste an invite link, or type the 5-letter code.';
        return;
      }
      host!.join(code);
      draw();
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') join();
    });
    secondaryButton('Join', { onClick: join }, row);
    primaryButton('Create party', {
      onClick: () => {
        host!.create();
        draw();
      },
    }, m.footer);
    setTimeout(() => input.focus(), 0);
  }

  function drawParty(party: PartyClient): void {
    m.body.replaceChildren();
    m.footer.replaceChildren();
    const room = party.room;
    // The invite link (on whatever host serves the game) is the way in; the code is a fallback.
    const link = inviteUrl(location.origin, party.code);
    const invite = el('div', 'kpt-invite', m.body);
    el('span', 'kpt-label', invite, 'Invite friends with this link');
    const field = el('input', 'kpt-link', invite);
    field.readOnly = true;
    field.value = link;
    field.addEventListener('focus', () => field.select());
    const actions = el('div', 'kpt-invite-actions', invite);
    primaryButton('Copy link', { className: 'kpt-copy', onClick: () => void copyLink(link, field) }, actions);
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (typeof nav.share === 'function') {
      secondaryButton('Share', { onClick: () => void nav.share!({ title: 'Join my Keystone party', url: link }).catch(() => undefined) }, actions);
    }
    const sub = el('div', 'kpt-sub', invite, `Code ${party.code}`);
    if (party.status !== 'open') el('span', 'kpt-status', sub, party.status === 'connecting' ? ' · Connecting…' : ' · Disconnected');
    if (!room) return void footerLeave(party);
    drawSlots(room, party.playerId);
    drawKey(room, party);
    if (room.blocked) el('p', 'kpt-blocked', m.body, room.blocked);
    footerLeave(party);
    const me = room.members.find((x) => x.playerId === party.playerId);
    if (!me) return;
    if (me.leader) {
      primaryButton(room.phase === 'starting' ? 'Starting…' : 'Start', {
        disabled: !!room.blocked || room.phase !== 'lobby',
        title: room.blocked ?? 'Start the key for everyone',
        onClick: () => party.send({ t: 'start' }),
      }, m.footer);
    } else {
      primaryButton(me.ready ? 'Not ready' : 'Ready', { onClick: () => party.send({ t: 'ready', on: !me.ready }) }, m.footer);
    }
  }

  function drawSlots(room: RoomView, you: string | null): void {
    const row = el('div', 'kpt-slots', m.body);
    for (const s of room.slots) {
      const mem = room.members.find((x) => x.playerId === s.playerId);
      const cell = el('div', `kpt-slot${mem ? ' human' : ' bot'}${mem?.playerId === you ? ' you' : ''}`, row);
      classCrest(s.classId, { size: 'md', selected: mem?.playerId === you, label: heroFor(s.classId).name }, cell);
      const who = el('div', 'kpt-who', cell);
      if (!mem) who.textContent = 'Bot';
      else {
        who.textContent = mem.playerId === you ? 'You' : 'Player';
        if (mem.leader) el('span', 'kpt-tag', who, 'Leader');
        if (!mem.connected) el('span', 'kpt-tag off', who, 'Offline');
        else if (!mem.leader) el('span', `kpt-tag${mem.ready ? ' ok' : ''}`, who, mem.ready ? 'Ready' : 'Not ready');
      }
    }
    for (const c of room.members.filter((x) => x.conflict)) {
      el('p', 'kpt-blocked', m.body, `${c.playerId === you ? 'You play' : 'A player plays'} ${heroFor(c.classId).name} too: pick another hero in the lobby (the party updates).`);
    }
  }

  function drawKey(room: RoomView, party: PartyClient): void {
    const row = el('div', 'kpt-key', m.body);
    const dungeon = ctx.content.dungeon(room.key.dungeonId)?.name ?? room.key.dungeonId;
    el('span', 'kpt-dungeon', row, dungeon);
    const leader = room.members.find((x) => x.leader)?.playerId === party.playerId;
    if (leader && room.phase === 'lobby') {
      stepper({
        value: room.key.level,
        min: 2,
        max: Math.max(2, room.maxKey),
        format: (v) => `+${v}`,
        onChange: (v) => party.send({ t: 'key', key: { ...room.key, level: v } }),
      }, row);
    } else el('span', 'kpt-level km-num', row, `+${room.key.level}`);
  }

  function footerLeave(_party: PartyClient): void {
    ghostButton('Leave party', {
      onClick: () => {
        host!.leave();
        draw();
      },
    }, m.footer);
  }

  draw();
}

/** Clipboard API, else the old selection + execCommand route, else leave the link selected. */
async function copyLink(link: string, field: HTMLInputElement): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
      kmToast('Invite link copied', 'success', 1600);
      return;
    }
  } catch {
    /* fall through */
  }
  field.focus();
  field.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  kmToast(ok ? 'Invite link copied' : 'Press Ctrl+C (⌘C) to copy the link', ok ? 'success' : 'info', 2200);
}
